import * as Sentry from "@sentry/nuxt";
import { waitForAppStartup } from "./app/composables/useAppStartup";

// Session Replay is added only once the app has started (see the bottom of this file), not in
// `init` itself: starting it snapshots the whole DOM and hooks every mutation from then on, and
// doing that during launch competed with the first screen for the main thread. Errors before it
// starts are still reported, just without a replay attached.
function createReplayIntegration() {
  return Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      // The message list is recorded as a blocked placeholder. Switching channels unmounts one full
      // page of messages and mounts another — thousands of nodes — and rrweb's mutation processing
      // for that (`isParentRemoved` walks every removed node's ancestors against the whole remove
      // list) profiled at ~980ms of main-thread JS, ~44% of all JS during channel switches.
      // `replaysOnErrorSampleRate: 1.0` keeps that recording running in the buffer for every
      // session, not just sampled ones. A childList mutation whose target sits inside a blocked
      // node is dropped at the top of rrweb's `processMutation`, so this cuts the whole cost
      // instead of just the serialization.
      // The group sidebar's channel tree (`[data-sentry-block]` in GroupInfo.vue) is blocked for the
      // same reason: switching groups replaces it wholesale, and its mutation processing showed up
      // inside the group-switch long task.
      block: ['[data-slot="message-scroller-content"]', '[data-sentry-block]'],
      unblock: ['.sentry-unblock', '[data-sentry-unblock]'],
      unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
  })
}

Sentry.init({
  dsn: "https://2ee577918b04d3d2ee0c1fcbadb3af81@sentry.shadum.com/14",

  // Tracing is deliberately OFF — `tracesSampleRate` is omitted entirely rather than set to `0`.
  //
  // @sentry/vue installs a global per-component tracing mixin whenever `hasSpansEnabled()` is true,
  // and that check is `tracesSampleRate != null` — `0` still counts as "set", so it would keep the
  // mixin (and its full cost) while collecting nothing. The sample rate only decides whether a
  // finished transaction is *sent*; the mixin runs on every component either way. It hooks
  // `beforeMount`/`mounted`/`activated`/`deactivated` on every instance and, on each hook, runs
  // `formatComponentName()` plus a `clearTimeout`/`setTimeout(..., 2000)` pair
  // (`maybeEndRootComponentSpan`). Opening a channel mounts ~2000 components at once, which
  // profiled as 12 096 timers installed inside a single ~700ms window plus ~200ms of extra
  // GC/microtask work — a visible stall while the message list still shows its skeleton.
  //
  // Error reporting and Session Replay are unaffected: `attachErrorHandler` and the replay
  // integration are installed independently of tracing. Re-enabling performance monitoring means
  // accepting that mixin cost again, so measure the channel-open path before doing so.
  //
  // tracesSampleRate: 1.0,

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/sentry\.shadum\.com\/api/],

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Replay starts once startup is over (app.vue, see useAppStartup.ts) and the browser is idle — the
// sample rates above still apply to it as usual.
// Not in dev: there `replaysOnErrorSampleRate: 1.0` keeps rrweb buffering every session, and Sentry
// as a whole was ~20% of all main-thread JS in a group-switch profile (rrweb's mutation observer
// being the part that scales with DOM churn) — skewing every performance recording made against
// the dev server.
if (!import.meta.dev) void waitForAppStartup().then(() => {
  const start = () => Sentry.addIntegration(createReplayIntegration())
  if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 3000 })
  else setTimeout(start, 1000)
})
